/**
 * Shared lookup tables for the 12 SRD classes.
 *
 * Centralised so that adding a new class is a single edit here, not a
 * hunt across every resolver that keys off `characterClass`. Consumers:
 *
 *   - `spells/scrollResolver.ts` (S12) — primary-ability lookup for
 *     universal-use scrolls.
 *   - `spells/magicalItemAffinity.ts` (S13) — tradition lookup for the
 *     matched/off-affinity damage scalar.
 *
 * Keep this module data-only: no resolver logic, no runtime side effects.
 */

import type { AbilityName } from "./entity.js";

export type SrdClassSlug =
  | "barbarian"
  | "bard"
  | "cleric"
  | "druid"
  | "fighter"
  | "monk"
  | "paladin"
  | "ranger"
  | "rogue"
  | "sorcerer"
  | "warlock"
  | "wizard";

/** Spell tradition an SRD class aligns with. `null` means no innate caster side. */
export type SpellTradition = "arcane" | "divine";

/** SRD class → primary spellcasting/attack ability. Drives scroll resolution (S12). */
export const CLASS_PRIMARY_ABILITY: Readonly<Record<SrdClassSlug, AbilityName>> = {
  barbarian: "STR",
  bard: "CHA",
  cleric: "WIS",
  druid: "WIS",
  fighter: "STR",
  monk: "DEX",
  paladin: "CHA",
  ranger: "DEX",
  rogue: "DEX",
  sorcerer: "CHA",
  warlock: "CHA",
  wizard: "INT",
};

/** Tradition each SRD class aligns with, or `null` when the class has no innate caster side. */
export const CLASS_TRADITION: Readonly<Record<SrdClassSlug, SpellTradition | null>> = {
  barbarian: null,
  bard: "arcane",
  cleric: "divine",
  druid: "divine",
  fighter: null,
  monk: null,
  paladin: "divine",
  ranger: "divine",
  rogue: null,
  sorcerer: "arcane",
  warlock: "arcane",
  wizard: "arcane",
};
